/* ==========================================================================
   Checkout Success Page
   Task F33: Return page after completing Stripe Checkout
   ========================================================================== */

import { CheckCircle, Mail, ClipboardList, Phone, Rocket } from 'lucide-react'
import { MarketingLayout } from '@/components/layout/MarketingLayout'

const STEPS = [
  {
    icon: Mail,
    title: 'Welcome email with intake form',
    description: 'Check your inbox for an email from TJM Labs with your unique intake form link.',
  },
  {
    icon: ClipboardList,
    title: 'Fill out the intake form',
    description: 'Tell us about your pharmacy — branding, formulary, preferred portal URL, and more.',
  },
  {
    icon: Phone,
    title: 'Onboarding call with TJM team',
    description: 'We\'ll walk you through the platform and make sure everything is configured to your needs.',
  },
  {
    icon: Rocket,
    title: 'Your portal goes live',
    description: 'Start inviting providers and receiving prescription requests through your branded portal.',
  },
]

export function CheckoutSuccess() {
  return (
    <MarketingLayout>
      <main className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" aria-hidden="true" />
            </div>
            <h1 className="font-serif text-3xl sm:text-4xl text-charcoal mb-3">
              Payment confirmed!
            </h1>
            <p className="text-lg text-charcoal/70">
              Thank you for choosing TJM Labs. Here's what happens next.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {STEPS.map((step, index) => {
              const Icon = step.icon
              const isLast = index === STEPS.length - 1

              return (
                <div key={step.title} className="flex gap-4 mb-8 last:mb-0">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-soft-lavender flex-shrink-0">
                      <Icon className="h-5 w-5 text-deep-purple" aria-hidden="true" />
                    </div>
                    {!isLast && (
                      <div className="w-px flex-1 bg-charcoal/10 mt-2" />
                    )}
                  </div>
                  <div className="pt-1 pb-4">
                    <p className="text-xs font-semibold text-deep-purple uppercase tracking-wide mb-1">
                      Step {index + 1}
                    </p>
                    <h3 className="font-semibold text-charcoal mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-charcoal/70">
                      {step.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-6">
            <Mail className="h-6 w-6 text-deep-purple mx-auto mb-3" aria-hidden="true" />
            <p className="font-semibold text-charcoal mb-1">
              Check your email for the intake form link
            </p>
            <p className="text-sm text-charcoal/60">
              Didn't receive it? Contact us at{' '}
              <a
                href="mailto:support@tjmlabs.com"
                className="text-deep-purple hover:underline"
              >
                support@tjmlabs.com
              </a>
            </p>
          </div>
        </div>
      </main>
    </MarketingLayout>
  )
}

export default CheckoutSuccess
